import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DialogData } from '../../../domain/common.model';
import { Offer } from '../../../domain/offer.domain';
import { Policy } from '../../../domain/policy.model';
import { FileService } from '../service/file.service';
import { GrowlService } from '../service/growl.service';
import { IOfferService } from '../service/ioffer.service';
import { DialogAction } from './dialog.action';
import { IOfferAction } from './ioffer.action';
import { RouterAction } from './router.action';

export class IOfferStateModel {
    wrongPesel: boolean;
    token: string;
    offer: Offer;
    policies: Policy[];
    inProgress: string[];
}

@State<IOfferStateModel>({
    name: 'ioffer',
    defaults: {
        wrongPesel: false,
        token: null,
        offer: null,
        policies: null,
        inProgress: []
    }
})

@Injectable()
export class IOfferState {

    constructor(private iOfferService: IOfferService,
                private fileService: FileService,
                private growlService: GrowlService,
    ) {
    }

    @Selector()
    static getWrongPesel(state: IOfferStateModel) {
        return state.wrongPesel;
    }

    @Selector()
    static getOffer(state: IOfferStateModel) {
        return state.offer;
    }

    @Selector()
    static getPolicies(state: IOfferStateModel) {
        return state.policies;
    }

    @Selector()
    static getInProgress(state: IOfferStateModel) {
        return state.inProgress;
    }

    @Action(IOfferAction.GetToken)
    getToken(context: StateContext<IOfferStateModel>, action: IOfferAction.GetToken) {
        return this.iOfferService.getOfferToken(action.token, action.pesel)
            .pipe(map(result => {
                context.patchState({token: result.headerToken, wrongPesel: false});
                context.dispatch(new RouterAction.Navigate(['/ioffer', 'offer'], {replaceUrl: true}))
            }), catchError(error => {
                context.patchState({wrongPesel: true});
                return of(error);
            }));
    }

    @Action(IOfferAction.GetOffer)
    getOffer(context: StateContext<IOfferStateModel>) {
        return this.iOfferService.getOffer(context.getState().token)
            .pipe(
                map(offer => context.patchState({offer})),
                catchError(() => {
                    context.dispatch(new RouterAction.Navigate(['/ioffer', 'no-state']));
                    return of(null);
                })
            );
    }

    @Action(IOfferAction.AcceptOffer)
    acceptOffer(context: StateContext<IOfferStateModel>) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'DIALOG.ACCEPT_OFFER';
        data.onConfirm = () => this.iOfferService.acceptOffer(context.getState().token)
            .pipe(
                map(holder => {
                    context.patchState({policies: holder.policyList});
                    context.dispatch(new RouterAction.Navigate(['/ioffer', 'policy'], {replaceUrl: true}));
                }),
            );
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(IOfferAction.CancelOffer)
    cancelOffer(context: StateContext<IOfferStateModel>) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'DIALOG.REJECT_OFFER';
        data.onConfirm = () => this.iOfferService.cancelOffer(context.getState().token)
            .pipe(map(() => context.dispatch(new RouterAction.Navigate(['/ioffer', 'rejected']))));
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(IOfferAction.PrintoutOffer)
    printoutOffer(context: StateContext<IOfferStateModel>, action: IOfferAction.PrintoutOffer) {
        return this.iOfferService.printoutOffer(context.getState().token, action.id, action.productId, action.all)
            .pipe(tap(file => {
                this.fileService.getIOfferFileByToken(file.token);
            }));
    }

    @Action(IOfferAction.PrintoutPolicy)
    printoutPolicy(context: StateContext<IOfferStateModel>, action: IOfferAction.PrintoutPolicy) {
        return this.iOfferService.printPolicy(context.getState().token, action.id, action.all)
            .pipe(tap(file => {
                this.fileService.getIOfferFileByToken(file.token);
            }));
    }

    @Action(IOfferAction.PrintoutSummary)
    printoutSummary(context: StateContext<IOfferStateModel>, action: IOfferAction.PrintoutSummary) {
        return this.iOfferService.printSummary(context.getState().token, action.id)
            .pipe(tap(file => {
                this.fileService.getIOfferFileByToken(file.token);
            }));
    }

    @Action(IOfferAction.PayPolicy)
    payPolicy(context: StateContext<IOfferStateModel>, action: IOfferAction.PayPolicy) {
        return this.iOfferService.payPolicy(context.getState().token, action.orderId)
            .pipe(tap(response => {
                if (response.redirecturl) {
                    window.open(response.redirecturl, '_blank');
                    const inProgress: string[] = cloneDeep(context.getState().inProgress);
                    if (inProgress.indexOf(action.orderId) === -1) {
                        inProgress.push(action.orderId);
                        context.patchState({inProgress});
                    }
                } else {
                    this.growlService.showErrorGrowl('ERROR.PAYMENT_FAILED');
                }
            }));
    }
}
